import { MessageBarType, Stack } from '@fluentui/react';
import { Button, buttonStylesGhost, useToast } from '@h2oai/ui-kit';

import {
  useMutationArchiveWorkspace,
  useMutationCreateWorkspace,
  useMutationDeleteWorkspace,
  useMutationUnarchiveWorkspace,
  useMutationUndeleteWorkspace,
} from '../../../authz/authz.hooks';
import { Workspace } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { formatError } from '../../../utils/utils';

export type WorkspaceMutations = {
  createWorkspace: (displayName: string) => void;
  deleteWorkspace: (wsName: string) => Promise<any>;
  undeleteWorkspace: (wsName: string) => Promise<any>;
  archiveWorkspace: (wsName: string) => Promise<any>;
  unarchiveWorkspace: (wsName: string) => Promise<any>;
};

/**
 * Hook that provides workspace mutation operations (create, delete, undelete)
 * Handles all mutation logic, error handling, and toast notifications
 */
export const useMutateWorkspace = (onWorkspaceCreated: (workspace: Workspace) => void): WorkspaceMutations => {
  const { addToast, removeAllToasts } = useToast();

  const undeleteWorkspaceMutation = useMutationUndeleteWorkspace({
    onSuccess: () => {
      removeAllToasts();
    },
    onError: (err) => {
      const message = `Failed to undelete workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ messageBarType: MessageBarType.error, message });
    },
  });

  const deleteWorkspaceMutation = useMutationDeleteWorkspace({
    onError: (err) => {
      const message = `Failed to delete workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ messageBarType: MessageBarType.error, message });
    },
    onSuccess: (_data, wsName) => {
      addToast({
        messageBarType: MessageBarType.success,
        message: (
          <Stack>
            <p>Workspace successfully deleted.</p>
            <Stack horizontalAlign="end">
              <Button onClick={() => undeleteWorkspaceMutation.mutate(wsName)} styles={buttonStylesGhost}>
                Undo
              </Button>
            </Stack>
          </Stack>
        ),
      });
    },
  });

  const createWorkspaceMutation = useMutationCreateWorkspace({
    onError: (err) => {
      const message = `Failed to create workspace: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
    onSuccess: ({ workspace: newWorkspace }) => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Workspace created successfully.',
      });
      if (newWorkspace) {
        onWorkspaceCreated(newWorkspace);
      }
    },
  });

  const archiveWorkspaceMutation = useMutationArchiveWorkspace({
    onError: (err) => {
      const message = `Failed to archive workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ messageBarType: MessageBarType.error, message });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Workspace archived successfully.',
      });
    },
  });

  const unarchiveWorkspaceMutation = useMutationUnarchiveWorkspace({
    onError: (err) => {
      const message = `Failed to unarchive workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ messageBarType: MessageBarType.error, message });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Workspace restored successfully.',
      });
    },
  });

  return {
    createWorkspace: (displayName: string) => {
      createWorkspaceMutation.mutate({
        workspace: { displayName, description: '' },
      });
    },
    deleteWorkspace: deleteWorkspaceMutation.mutateAsync,
    undeleteWorkspace: undeleteWorkspaceMutation.mutateAsync,
    archiveWorkspace: archiveWorkspaceMutation.mutateAsync,
    unarchiveWorkspace: unarchiveWorkspaceMutation.mutateAsync,
  };
};
